/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useEffect } from 'react';
import { useQuery } from '@apollo/react-hooks';

import { isUnauthorizedError } from '@confluence/error-boundary';
import { markErrorAsHandled } from '@confluence/graphql';
import { useSpaceKey } from '@confluence/space-utils';
import { useIsExternalCollaborator } from '@confluence/external-collab-ui/entry-points/useIsExternalCollaborator';

import type {
	usePublicLinkContentQueryPublicLinkContentQuery as usePublicLinkContentQueryPublicLinkContentQuery$data,
	usePublicLinkContentQueryPublicLinkContentQueryVariables as usePublicLinkContentQueryPublicLinkContentQuery$variables,
} from './__types__/usePublicLinkContentQueryPublicLinkContentQuery';
export type PublicLinkContentData = {
	contentType: string;
	canEditPublicLink: boolean;
};

export type PublicLinkContentQueryData = {
	isLoading: boolean;
	error?: Error;
	data?: PublicLinkContentData;
};

const computeData = (
	data: usePublicLinkContentQueryPublicLinkContentQuery$data | undefined,
	isExternalCollaborator: boolean,
): PublicLinkContentData => {
	const contentType = data?.content?.nodes?.[0]?.type || '';
	const operations = data?.content?.nodes?.[0]?.operations || [];
	const isSpaceAdmin = Boolean(data?.space?.currentUser.isAdmin);
	const canEditPage =
		operations.filter(
			(operationCheckResult) =>
				operationCheckResult?.operation === 'update' &&
				operationCheckResult?.targetType === contentType,
		).length > 0;

	return {
		contentType,
		canEditPublicLink: (isSpaceAdmin || canEditPage) && !isExternalCollaborator,
	};
};

export const usePublicLinkContentQuery = (props: {
	contentId: string;
	skip?: boolean;
}): PublicLinkContentQueryData => {
	const spaceKey = useSpaceKey();
	const { isExternalCollaborator } = useIsExternalCollaborator();
	const { loading, data, error } = useQuery<
		usePublicLinkContentQueryPublicLinkContentQuery$data,
		usePublicLinkContentQueryPublicLinkContentQuery$variables
	>(
		gql`
			query usePublicLinkContentQueryPublicLinkContentQuery(
				$contentId: ID!
				$spaceKey: String!
				$hasSpaceKey: Boolean!
			) {
				content(id: $contentId) {
					nodes {
						id
						type
						operations {
							operation
							targetType
						}
					}
				}
				space(key: $spaceKey) @include(if: $hasSpaceKey) {
					id
					currentUser {
						isAdmin
					}
				}
			}
		`,
		{
			skip: props.skip,
			variables: {
				contentId: props.contentId,
				hasSpaceKey: Boolean(spaceKey),
				spaceKey: spaceKey ?? '',
			},
		},
	);
	useEffect(() => {
		if (isUnauthorizedError(error)) markErrorAsHandled(error);
	}, [error]);

	return loading
		? { isLoading: true }
		: {
				isLoading: false,
				error,
				data: error ? undefined : computeData(data, isExternalCollaborator),
			};
};
